<template>
  <div class="app-shell">
    <aside :class="['sidebar', { 'mobile-open': mobileMenuOpen }]">
      <div class="brand">
        <div class="brand-left">
          <img class="brand-mark" src="@/assets/xblogo.png" alt="煋邦" />
          <div>
            <div class="brand-title">煋邦智慧管控系统</div>
            <div class="brand-subtitle">Work and Device Control</div>
          </div>
        </div>
        <button class="mobile-menu-toggle" type="button" @click="mobileMenuOpen = !mobileMenuOpen">
          {{ mobileMenuOpen ? '收起' : '菜单' }}
        </button>
      </div>

      <el-menu
        :default-active="$route.path"
        :default-openeds="defaultOpeneds"
        background-color="transparent"
        text-color="#b7c3d8"
        active-text-color="#ffffff"
        router
        unique-opened
        @select="handleMenuSelect"
      >
        <menu-item
          v-for="route in menuRoutes"
          :key="route.resolvedPath"
          :route="route"
        />
      </el-menu>
    </aside>

    <main class="main">
      <header class="topbar">
        <div>
          <div class="title">{{ currentTitle }}</div>
          <div class="subtitle">企业、地区、设备、作业记录统一归集与追溯</div>
        </div>

        <div class="topbar-right">
          <span class="scope-pill">{{ scopeLabel }}</span>
          <span class="clock-pill">{{ nowText }}</span>
          <div class="user-box">
            <span>{{ name || '管理员' }}</span>
            <el-button type="text" @click="passwordDialogVisible = true">修改密码</el-button>
            <el-button type="text" @click="handleLogout">退出</el-button>
          </div>
        </div>
      </header>

      <section class="content">
        <router-view />
      </section>
    </main>

    <el-dialog title="修改密码" :visible.sync="passwordDialogVisible" width="420px">
      <el-form label-width="90px">
        <el-form-item label="原密码">
          <el-input v-model="passwordForm.old_password" type="password" show-password />
        </el-form-item>
        <el-form-item label="新密码">
          <el-input v-model="passwordForm.new_password" type="password" show-password />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="passwordDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="passwordSaving" @click="handleChangePassword">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { constantRoutes } from '@/router'
import { changeMyPassword } from '@/api/user'
import MenuItem from './MenuItem.vue'

export default {
  name: 'Layout',
  components: { MenuItem },
  data() {
    return {
      nowText: '',
      mobileMenuOpen: false,
      passwordDialogVisible: false,
      passwordSaving: false,
      passwordForm: {
        old_password: '',
        new_password: ''
      }
    }
  },
  computed: {
    menuRoutes() {
      return constantRoutes
        .filter(route => route.path !== '/login' && !route.hidden)
        .map(route => this.normalizeMenuRoute(route, ''))
        .filter(Boolean)
    },
    defaultOpeneds() {
      const paths = []
      const collect = route => {
        if (route.children && route.children.length) {
          paths.push(route.resolvedPath)
          route.children.forEach(collect)
        }
      }
      this.menuRoutes.forEach(collect)
      return paths
    },
    name() {
      return this.$store.getters.name
    },
    scopeLabel() {
      return `当前角色：${this.$store.getters.roleLabel || '管理员'}`
    },
    currentTitle() {
      return this.$route.meta.title || '首页'
    }
  },
  created() {
    this.tick()
    this.timer = window.setInterval(this.tick, 1000)
  },
  beforeDestroy() {
    window.clearInterval(this.timer)
  },
  methods: {
    normalizeMenuRoute(route, parentPath) {
      if (route.hidden) return null
      const resolvedPath = this.resolvePath(parentPath, route.path)
      const originalChildren = route.children || []
      if (originalChildren.length) {
        const children = originalChildren
          .map(child => this.normalizeMenuRoute(child, resolvedPath))
          .filter(Boolean)
        if (!children.length) return null
        if (children.length === 1 && !(route.meta && route.meta.alwaysShow)) {
          return children[0]
        }
        return { ...route, resolvedPath, children }
      }
      if (!this.hasMenuPermission(route)) return null
      return { ...route, resolvedPath, children: [] }
    },
    hasMenuPermission(route) {
      const menus = (this.$store.getters.permissions && this.$store.getters.permissions.menus) || []
      const menuKey = route.meta && route.meta.menuKey
      return !menuKey || menus.includes(menuKey)
    },
    resolvePath(parent, child) {
      return `${parent}/${child}`.replace(/\/+/g, '/')
    },
    tick() {
      const now = new Date()
      const pad = value => String(value).padStart(2, '0')
      this.nowText = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    },
    handleLogout() {
      this.$store.dispatch('logout')
      this.$router.push('/login')
    },
    async handleChangePassword() {
      if (!this.passwordForm.old_password || !this.passwordForm.new_password) {
        this.$message.warning('请输入原密码和新密码')
        return
      }
      if (this.passwordForm.new_password.length < 6) {
        this.$message.warning('新密码至少6位')
        return
      }
      this.passwordSaving = true
      try {
        await changeMyPassword(this.passwordForm)
        this.$message.success('密码已修改，请重新登录')
        this.passwordDialogVisible = false
        this.passwordForm = { old_password: '', new_password: '' }
        this.handleLogout()
      } finally {
        this.passwordSaving = false
      }
    },
    handleMenuSelect() {
      this.mobileMenuOpen = false
    }
  }
}
</script>

<style scoped>
.app-shell {
  display: flex;
  min-height: 100vh;
}

.sidebar {
  width: 248px;
  flex-shrink: 0;
  padding: 18px 14px;
  background:
    radial-gradient(circle at top right, rgba(86, 166, 255, 0.16), transparent 24%),
    linear-gradient(180deg, var(--nav-bg), #101a2e 100%);
}

.brand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 72px;
  padding: 0 12px 14px;
  margin-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.brand-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.brand-mark {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  object-fit: contain;
  background: rgba(255, 255, 255, 0.08);
}

.brand-title {
  color: #fff;
  font-size: 15px;
  font-weight: 800;
}

.brand-subtitle {
  margin-top: 3px;
  color: rgba(183, 195, 216, 0.75);
  font-size: 11px;
}

.mobile-menu-toggle {
  display: none;
  align-items: center;
  justify-content: center;
  min-width: 62px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 13px;
}

.main {
  flex: 1;
  min-width: 0;
}

.topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 72px;
  padding: 0 24px;
  background: rgba(255, 255, 255, 0.72);
  border-bottom: 1px solid var(--line);
  backdrop-filter: blur(16px);
}

.title {
  font-size: 20px;
  font-weight: 800;
}

.subtitle {
  margin-top: 4px;
  color: var(--muted);
  font-size: 12px;
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.scope-pill,
.clock-pill {
  display: inline-flex;
  min-height: 34px;
  align-items: center;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.76);
  color: var(--muted);
  font-size: 12px;
}

.user-box {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-left: 8px;
  color: var(--text);
}

.content {
  min-height: calc(100vh - 72px);
}

.el-menu {
  border-right: none;
}

.el-menu-item,
.el-submenu /deep/ .el-submenu__title {
  height: 44px;
  line-height: 44px;
  border-radius: 12px;
  margin-bottom: 6px;
}

.el-menu-item.is-active,
.el-submenu /deep/ .el-menu-item.is-active {
  background: rgba(255, 255, 255, 0.12) !important;
}

@media (max-width: 920px) {
  .app-shell {
    flex-direction: column;
  }

  .sidebar {
    width: 100%;
    padding: 10px 10px 6px;
  }

  .brand {
    min-height: 50px;
    padding: 0 6px 8px;
    margin-bottom: 4px;
  }

  .brand-left {
    gap: 10px;
  }

  .brand-mark {
    width: 30px;
    height: 30px;
    border-radius: 8px;
  }

  .brand-title {
    font-size: 14px;
  }

  .brand-subtitle {
    display: none;
  }

  .mobile-menu-toggle {
    display: inline-flex;
  }

  .el-menu {
    display: none;
    max-height: 58vh;
    overflow-y: auto;
    padding: 4px 0 6px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }

  .sidebar.mobile-open .el-menu {
    display: block;
  }

  .el-menu-item,
  .el-submenu /deep/ .el-submenu__title {
    height: 40px;
    line-height: 40px;
    margin-bottom: 4px;
    border-radius: 10px;
  }

  .el-submenu /deep/ .el-menu {
    display: block;
    max-height: none;
    padding: 0 0 0 14px;
    border-top: none;
    overflow: visible;
  }

  .el-submenu /deep/ .el-menu-item {
    min-width: 0;
    padding-left: 42px !important;
  }

  .topbar {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
    min-height: auto;
    padding: 12px;
  }

  .title {
    font-size: 18px;
  }

  .subtitle {
    display: none;
  }

  .topbar-right {
    flex-wrap: wrap;
    gap: 6px;
  }

  .scope-pill,
  .clock-pill {
    min-height: 30px;
    padding: 0 10px;
  }

  .content {
    min-height: auto;
  }
}

@media (max-width: 520px) {
  .scope-pill {
    display: none;
  }

  .user-box {
    padding-left: 0;
  }
}
</style>
